import { buildSearchPayload } from '@civilgrid/ui/hooks';
import type { FilterGroupItem } from '@civilgrid/ui/utils';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type BulkConstructionDatesResponse,
  type BulkConstructionDatesSelection,
  type BulkConstructionDatesUpdates,
  type BulkPlanStatusResponse,
  type BulkPlanStatusUpdates,
  type BulkSummariesSelection,
  bulkUpdateConstructionDates,
  bulkUpdatePlanStatus,
  exportIntelligenceSummariesV3,
  fetchIntelligenceSummaries,
  fetchIntelligenceSummaryMeta,
  type SummariesSearchPayload,
  type SummaryExportColumnDef,
} from '~/api/intelligenceSummariesV3';
import { downloadExcelFileBuffer } from '~/common/exportUtils';
import type { IntelligenceSummary } from '~/common/types';

/** Root query key for all Conflict Summary v3 queries (list, meta, poll). */
export const SUMMARIES_QUERY_KEY = 'intelligenceSummaries';

const POLL_INTERVAL_MS = 5_000;

export type UseGetSummariesParams = {
  organizationId: string;
  pageIndex?: number;
  pageSize?: number;
  sorting?: { id: string; desc: boolean }[];
  filters?: FilterGroupItem;
  globalFilter?: string;
  enabled?: boolean;
};

/** Build the v3 search payload (limit/skip/sort/filters/globalFilter) from table state. */
export function buildSummariesSearchPayload(
  params: Omit<UseGetSummariesParams, 'organizationId' | 'enabled'>,
): SummariesSearchPayload {
  const { pageIndex = 0, pageSize = 100, sorting, filters, globalFilter } = params;
  return buildSearchPayload({ pageIndex, pageSize, sorting, filters, globalFilter });
}

/**
 * Ids worth registering with addToPoll after a bulk apply: the poll query
 * serializes every id into a GET query string, so a whole select-all result
 * set would overrun URL limits and 414 forever. Off-page rows are picked up
 * by getPollIds once they come into view.
 */
export function visiblePollIds(summaryIds: string[], summaries: IntelligenceSummary[] | undefined): string[] {
  const visible = new Set((summaries ?? []).map((s) => s.id));
  return summaryIds.filter((id) => visible.has(id));
}

/** Ids that still need polling: rows the server reports as processing, plus any registered after an edit. */
export function getPollIds(summaries: IntelligenceSummary[] | undefined, extraPollIds: string[]): string[] {
  const processingIds = (summaries ?? []).filter((s) => s.computeStatus === 'processing').map((s) => s.id);
  return Array.from(new Set([...processingIds, ...extraPollIds])).sort();
}

/** Polled rows that finished computing (any status other than processing). */
export function getFinishedIds(polled: IntelligenceSummary[]): string[] {
  return polled.filter((s) => s.computeStatus !== 'processing').map((s) => s.id);
}

/**
 * Completions from a poll response that haven't been handled yet. Invalidating
 * [SUMMARIES_QUERY_KEY] refetches the poll query itself, and the refetched rows
 * keep their finished statuses until the list query settles — so each id may
 * invalidate only once. `nextHandledIds` re-arms any id that reports
 * `processing` again (a new recompute) and absorbs the fresh completions.
 */
export function resolveFinishedPolls(
  polled: IntelligenceSummary[],
  handledIds: ReadonlySet<string>,
): { finishedIds: string[]; nextHandledIds: Set<string> } {
  const nextHandledIds = new Set(handledIds);
  for (const summary of polled) {
    if (summary.computeStatus === 'processing') nextHandledIds.delete(summary.id);
  }
  const finishedIds = getFinishedIds(polled).filter((id) => !nextHandledIds.has(id));
  for (const id of finishedIds) nextHandledIds.add(id);
  return { finishedIds, nextHandledIds };
}

/**
 * Fetch the summaries page for the current table state. Same TanStack Query
 * defaults as the homeViewV2 projects table (placeholder previous page,
 * staleTime 5m, no refetch on focus).
 */
export function useGetIntelligenceSummaries(params: UseGetSummariesParams) {
  const { organizationId, enabled = true, ...searchParams } = params;

  const searchPayload = buildSummariesSearchPayload(searchParams);

  return useQuery({
    queryKey: [SUMMARIES_QUERY_KEY, { organizationId, searchPayload }],
    queryFn: ({ signal }) => fetchIntelligenceSummaries(organizationId, searchPayload, { signal }),
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!organizationId,
  });
}

/** Warm the cache for a summaries page (e.g. on shortcut hover); same key and fetch as useGetIntelligenceSummaries. */
export function prefetchSummariesQuery(queryClient: QueryClient, params: UseGetSummariesParams) {
  const { organizationId, enabled, ...searchParams } = params;
  if (!organizationId || enabled === false) return;
  const searchPayload = buildSummariesSearchPayload(searchParams);
  void queryClient.prefetchQuery({
    queryKey: [SUMMARIES_QUERY_KEY, { organizationId, searchPayload }],
    queryFn: ({ signal }) => fetchIntelligenceSummaries(organizationId, searchPayload, { signal }),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Org-wide table metadata (total count + distinct construction years). Keyed
 * on the org only — values are unfiltered by design, so table filter changes
 * never refetch this.
 */
export function useGetIntelligenceSummaryMeta(organizationId: string) {
  return useQuery({
    queryKey: [SUMMARIES_QUERY_KEY, 'meta', organizationId],
    queryFn: ({ signal }) => fetchIntelligenceSummaryMeta(organizationId, { signal }),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    enabled: !!organizationId,
  });
}

/**
 * Invalidate every Conflict Summary query (list pages, meta, poll).
 * Call after any edit so the table and year options refetch together.
 */
export function useInvalidateSummaries() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [SUMMARIES_QUERY_KEY] });
  }, [queryClient]);
}

/**
 * Poll rows stuck in `computeStatus: processing` through the v3 search with
 * `summaryIds` narrowing. Rows to poll come from the current page's data plus
 * any ids registered via `addToPoll`. When a polled row finishes, all summary
 * queries are invalidated (once per completion).
 */
export function useSummaryPolling({
  organizationId,
  summaries,
}: {
  organizationId: string;
  summaries: IntelligenceSummary[] | undefined;
}) {
  const queryClient = useQueryClient();
  const [extraPollIds, setExtraPollIds] = useState<string[]>([]);
  // Ids whose completion has already triggered an invalidation — without this
  // guard each poll refetch would re-invalidate (cancelling the in-flight list
  // refetch) in a self-sustaining loop. See resolveFinishedPolls.
  const handledIdsRef = useRef<Set<string>>(new Set());

  const pollIds = useMemo(() => getPollIds(summaries, extraPollIds), [summaries, extraPollIds]);

  const { data: polled } = useQuery({
    queryKey: [SUMMARIES_QUERY_KEY, 'poll', { organizationId, pollIds }],
    queryFn: async ({ signal }) => {
      const { summaries: polledSummaries } = await fetchIntelligenceSummaries(
        organizationId,
        { limit: pollIds.length, skip: 0, summaryIds: pollIds },
        { signal },
      );
      return polledSummaries;
    },
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
    enabled: !!organizationId && pollIds.length > 0,
  });

  useEffect(() => {
    if (!polled) return;
    const { finishedIds, nextHandledIds } = resolveFinishedPolls(polled, handledIdsRef.current);
    handledIdsRef.current = nextHandledIds;
    if (finishedIds.length === 0) return;
    setExtraPollIds((prev) => prev.filter((id) => !finishedIds.includes(id)));
    void queryClient.invalidateQueries({ queryKey: [SUMMARIES_QUERY_KEY] });
  }, [polled, queryClient]);

  const addToPoll = useCallback((summaryId: string) => {
    handledIdsRef.current.delete(summaryId);
    setExtraPollIds((prev) => (prev.includes(summaryId) ? prev : [...prev, summaryId]));
  }, []);

  return { addToPoll };
}

/**
 * Bulk-apply construction dates to the selected summaries' projects. The server
 * dispatches a recompute per project; on success the caller polls the returned
 * summary ids and invalidates the summary queries.
 */
export function useBulkUpdateConstructionDates(organizationId: string) {
  return useMutation<
    BulkConstructionDatesResponse,
    Error,
    { selection: BulkConstructionDatesSelection; updates: BulkConstructionDatesUpdates }
  >({
    mutationKey: ['conflict-summary-v3-bulk-construction-dates', { organizationId }],
    mutationFn: (payload) => bulkUpdateConstructionDates(organizationId, payload),
  });
}

export function useBulkUpdatePlanStatus(organizationId: string) {
  return useMutation<
    BulkPlanStatusResponse,
    Error,
    { selection: BulkSummariesSelection; updates: BulkPlanStatusUpdates }
  >({
    mutationKey: ['conflict-summary-v3-bulk-plan-status', { organizationId }],
    mutationFn: (payload) => bulkUpdatePlanStatus(organizationId, payload),
  });
}

/**
 * Export the summaries table to Excel server-side (all rows matching the
 * search payload; the server adds one sheet per construction year).
 */
export function useExportSummariesTable(organizationId: string, organizationName?: string) {
  return useMutation<void, Error, { columnDefs: SummaryExportColumnDef[]; search: SummariesSearchPayload }>({
    mutationKey: ['conflict-summary-v3-excel-export', { organizationId }],
    mutationFn: (payload) =>
      exportIntelligenceSummariesV3(organizationId, payload).then((buffer) =>
        downloadExcelFileBuffer(
          buffer as unknown as Buffer,
          `Conflicts-Summary_${organizationName ?? ''}_${new Date().toLocaleString()}`,
        ),
      ),
  });
}
